"use client";

import { useState } from "react";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseLineList } from "@/lib/tool-kit/study/line-list";

const FREE_CELL = "\u0000free\u0000";
const CARD_COUNTS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const SIZE_OPTIONS: { value: number; label: string }[] = [
  { value: 3, label: "3 × 3" },
  { value: 4, label: "4 × 4" },
  { value: 5, label: "5 × 5" },
];

function shuffle<T>(items: T[]): T[] {
  const result = items.slice();
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = result[i] as T;
    result[i] = result[j] as T;
    result[j] = a;
  }
  return result;
}

export function buildCard(
  terms: string[],
  size: number,
  freeCenter: boolean
): string[][] {
  const useFree = freeCenter && size % 2 === 1;
  const needed = size * size - (useFree ? 1 : 0);
  const sample = shuffle(terms).slice(0, needed);
  const cells: string[] = [];
  const centerIndex = Math.floor((size * size) / 2);
  let sampleIndex = 0;
  for (let i = 0; i < size * size; i += 1) {
    if (useFree && i === centerIndex) {
      cells.push(FREE_CELL);
    } else {
      cells.push(sample[sampleIndex] ?? "");
      sampleIndex += 1;
    }
  }
  return Array.from({ length: size }, (_, r) =>
    cells.slice(r * size, (r + 1) * size)
  );
}

export function bingoCellLabel(cell: string, freeLabel: string): string {
  return cell === FREE_CELL ? freeLabel : cell;
}

function buildCards(
  terms: string[],
  size: number,
  freeCenter: boolean,
  count: number
): string[][][] {
  return Array.from({ length: count }, () =>
    buildCard(terms, size, freeCenter)
  );
}

function neededTerms(size: number, freeCenter: boolean): number {
  return size * size - (freeCenter && size % 2 === 1 ? 1 : 0);
}

/** English strings. A translated content file overrides these through `ui`. */
export const BINGO_CARD_GENERATOR_STRINGS = {
  wordsOrTermsOnePer: "Words or terms — one per line",
  photosynthesisMitosisOsmosisEnzymeNucleus:
    "photosynthesis\nmitosis\nosmosis\nenzyme\nnucleus",
  term: "one: term|other: terms",
  needSummary: "{terms} — need at least {needed} for a {grid} card",
  cardSize: "Card size",
  freeSpaceInCenterOdd: "Free space in center (odd sizes only)",
  freeLabel: "FREE",
  numberOfCards: "Number of cards",
  regenerate: "Regenerate",
  generateCards: "Generate Cards",
  print: "Print",
  bingoCard: "BINGO — Card ",
  duplicatesRemoved:
    "one: {count} duplicate removed|other: {count} duplicates removed",
  moreNeeded:
    "one: ({count} more term needed)|other: ({count} more terms needed)",
  worksForClassroomVocabularyReview:
    "Works for classroom vocabulary review, sight words, conference\n        icebreakers, baby showers, and event bingo — the cards contain exactly\n        the terms you type, shuffled per card. Calling the game is up to you.",
} as const;

export default function BingoCardGeneratorWidget() {
  const [input, setInput] = useState("");
  const [size, setSize] = useState(5);
  const [freeCenter, setFreeCenter] = useState(true);
  const [cardCount, setCardCount] = useState(4);
  const [cards, setCards] = useState<string[][][]>([]);
  const [cardSize, setCardSize] = useState(5);

  const { items: terms, removed: duplicates } = parseLineList(input);
  const needed = neededTerms(size, freeCenter);
  const shortBy = Math.max(needed - terms.length, 0);
  const freeAllowed = size % 2 === 1;

  function handleGenerate() {
    if (shortBy > 0) {
      return;
    }
    setCards(buildCards(terms, size, freeCenter, cardCount));
    setCardSize(size);
  }

  const locale = useToolLocale();
  const strings = useToolUi(BINGO_CARD_GENERATOR_STRINGS);

  return (
    <>
      {/* Term list */}
      <div className="space-y-2 print:hidden">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="term-input"
        >
          {strings.wordsOrTermsOnePer}
        </label>
        <textarea
          className="min-h-[180px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="term-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.photosynthesisMitosisOsmosisEnzymeNucleus}
          value={input}
        />
        <p className="text-muted-foreground text-xs">
          {t(strings.needSummary, {
            terms: `${terms.length}${tPlural(strings.term, locale, terms.length)}`,
            needed,
            grid: `${size}×${size}`,
          })}
          {shortBy > 0
            ? tPlural(strings.moreNeeded, locale, shortBy, { count: shortBy })
            : ""}
        </p>
        {duplicates > 0 && (
          <p className="text-muted-foreground text-xs">
            {tPlural(strings.duplicatesRemoved, locale, duplicates, {
              count: duplicates,
            })}
          </p>
        )}
      </div>
      {/* Options */}
      <section className="space-y-4 print:hidden">
        <div className="space-y-3">
          <p className="font-medium text-foreground text-sm">
            {strings.cardSize}
          </p>
          <div className="flex flex-wrap gap-3">
            {SIZE_OPTIONS.map((o) => (
              <label
                className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
                key={o.value}
              >
                <input
                  checked={size === o.value}
                  className="accent-primary"
                  name="card-size"
                  onChange={() => setSize(o.value)}
                  type="radio"
                  value={o.value}
                />
                {o.label}
              </label>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <label
            className={`flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5 ${freeAllowed ? "cursor-pointer" : "opacity-50"}`}
          >
            <input
              checked={freeCenter && freeAllowed}
              className="accent-primary"
              disabled={!freeAllowed}
              onChange={(e) => setFreeCenter(e.target.checked)}
              type="checkbox"
            />
            {strings.freeSpaceInCenterOdd}
          </label>
          <label
            className="flex items-center gap-2 text-foreground text-sm"
            htmlFor="card-count"
          >
            {strings.numberOfCards}
            <select
              className="rounded-lg border border-border bg-card px-3 py-2 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="card-count"
              onChange={(e) => setCardCount(Number(e.target.value))}
              value={cardCount}
            >
              {CARD_COUNTS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>
      {/* Actions */}
      <div className="flex flex-wrap gap-3 print:hidden">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={terms.length === 0 || shortBy > 0}
          onClick={handleGenerate}
          type="button"
        >
          {cards.length > 0 ? strings.regenerate : strings.generateCards}
        </button>
        {cards.length > 0 && (
          <button
            className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted"
            onClick={() => window.print()}
            type="button"
          >
            {strings.print}
          </button>
        )}
      </div>
      {/* Cards (printable) */}
      {cards.length > 0 && (
        <section className="grid grid-cols-1 gap-8 md:grid-cols-2 print:grid-cols-1">
          {cards.map((card, cardIndex) => (
            <div
              className="break-inside-avoid space-y-2"
              key={`card-${cardIndex + 1}-${card[0]?.join("|") ?? ""}`}
            >
              <p className="text-center font-semibold text-foreground text-sm tracking-wide">
                {strings.bingoCard}
                {cardIndex + 1}
              </p>
              <table className="w-full table-fixed border-collapse">
                <tbody>
                  {card.map((row, r) => (
                    <tr key={`card-${cardIndex + 1}-row-${r + 1}`}>
                      {row.map((cell, c) => {
                        const isFree = cell === FREE_CELL;
                        return (
                          <td
                            className={`h-16 break-words border border-border p-1 text-center text-foreground text-xs leading-tight ${isFree ? "bg-muted/50 font-bold" : ""} ${cardSize === 3 ? "h-20" : ""}`}
                            key={`card-${cardIndex + 1}-cell-${r + 1}-${c + 1}`}
                          >
                            {bingoCellLabel(cell, strings.freeLabel)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </section>
      )}
      {/* Honest note */}
      <p className="text-muted-foreground text-sm leading-relaxed print:hidden">
        {strings.worksForClassroomVocabularyReview}
      </p>
    </>
  );
}
